import { useEffect, useState } from 'react';
import apiClient from '../api/client';

// All units in the run participate, independently of table filters or plot selection.
export default function useCuratorAmplitudes(run, confirmed, recordingName, revision, retry, onChannels) {
  const [state, setState] = useState({});
  useEffect(() => {
    if (!run || !confirmed) return undefined;
    const controller = new AbortController();
    const scope = { run, recordingName, revision, retry };
    const profiles = {}, errors = {}, channels = {};
    const publish = (completed, loading) => setState({ ...scope, profiles: { ...profiles },
      errors: { ...errors }, channels: { ...channels }, completed, loading });
    publish(0, true);
    const load = async () => {
      for (let start = 0; start < run.clusters.length; start += 4) {
        const batch = run.clusters.slice(start, start + 4);
        try {
          const missing = batch.filter(unit => unit.primaryChannel == null && unit.spikeTimes.length);
          if (missing.length) {
            const response = await apiClient.predictPrimaryChannels(missing.map(unit => ({
              id: unit.id, primaryChannel: null, spikeTimes: unit.spikeTimes,
            })), 128);
            if (controller.signal.aborted) return;
            if (response.data?.dataset && response.data.dataset !== recordingName) throw new Error('The active recording changed. Confirm the matching recording again.');
            (response.data?.clusters || []).forEach(unit => {
              if (missing.some(original => String(original.id) === String(unit.id)) && Number.isInteger(unit.primaryChannel) && unit.primaryChannel > 0) channels[unit.id] = unit.primaryChannel;
            });
          }
        } catch (error) {
          if (controller.signal.aborted) return;
          batch.filter(unit => unit.primaryChannel == null).forEach(unit => { errors[unit.id] = error.message; });
        }
        if (controller.signal.aborted) return;
        const ready = batch.filter(unit => {
          if (unit.primaryChannel == null && channels[unit.id] == null) {
            errors[unit.id] ||= 'Primary channel unavailable. Provide a channel or retry prediction.';
            return false;
          }
          return true;
        });
        try {
          if (ready.length) {
            // Deliberately omit supplied amplitudes: all compared units use raw
            // peak-to-peak amplitudes in the recording's units, with the same window.
            const response = await apiClient.getClusterAmplitudeProfiles(ready.map(unit => unit.id), '',
              ready.map(unit => ({ id: unit.id, primaryChannel: unit.primaryChannel ?? channels[unit.id], spikeTimes: unit.spikeTimes })),
              { signal: controller.signal, recordingName });
            if (controller.signal.aborted) return;
            ready.forEach(unit => {
              const profile = response.profiles?.find(item => String(item.id) === String(unit.id));
              const values = profile?.values;
              if (!Array.isArray(values) || values.length !== unit.spikeTimes.length || !values.length ||
                values.some(value => typeof value !== 'number' || !Number.isFinite(value))) {
                errors[unit.id] = `Complete amplitudes unavailable (${values?.length || 0} of ${unit.spikeTimes.length} spikes). Check recording bounds and timestamps.`;
              } else profiles[unit.id] = values;
            });
          }
        } catch (error) {
          if (controller.signal.aborted) return;
          ready.forEach(unit => { errors[unit.id] = error.message || 'Unable to extract amplitudes.'; });
        }
        if (controller.signal.aborted) return;
        publish(Math.min(start + batch.length, run.clusters.length), start + batch.length < run.clusters.length);
      }
      if (!controller.signal.aborted && Object.keys(channels).length) onChannels?.(channels);
    };
    load();
    return () => controller.abort();
  }, [run, confirmed, recordingName, revision, retry, onChannels]);
  return confirmed && state.run === run && state.recordingName === recordingName && state.revision === revision && state.retry === retry
    ? state : {};
}
